import { useCallback, useEffect, useState } from "react";

import API from "Payments/PaymentPage/API";
import { usePaymentPage } from "Payments/PaymentPage/PaymentPageContext";

export function usePollForPaymentStatus({
  setIsSubmitting,
  setSubmitError,
  forScreen, // "paymentForm", "modifyOrCancel"
}) {
  const { state, dispatch } = usePaymentPage();
  // const [shouldPoll, setShouldPoll] = useState(false);
  const [pollCount, setPollCount] = useState(0);
  const [declinedPaymentId, setDeclinedPaymentId] = useState();

  const { currentScreen } = state;

  // this hook is used on both the modifyOrCancel & paymentForm screen, so the
  // we do this isForCurrentScreen check to make sure only the hook for the visible screen runs,
  // otherwise we end up with both hooks/screens polling for a response.
  const isForCurrentScreen = currentScreen === forScreen;

  const NUM_SECONDS_TO_POLL =
    window?.data?.paymentPageData?.maxSecondsToPollPaymentStatus || 5;

  const { paymentID } = state.submitPaymentResponse;
  const { paymentStatus } = state.pollingPaymentResponse;
  const paymentHasBeenSubmitted = state.submitPaymentResponse?.success;
  const paymentResultIsResolved =
    paymentStatus === "success" || paymentStatus === "failed";
  const helpEmail = state.network.contactEmail;
  // const paymentIsRecurring = state.recurringPayment?.recurring;
  const recurringPaymentsOnlySuccess = isRecurringPaymentsOnlySuccess(
    state.submitPaymentResponse,
    state.recurringPayment?.startDate
  );

  const paymentIDAlreadyFailed = declinedPaymentId === paymentID;

  const contactUs = useCallback(() => {
    return (
      <span>
        contact <a href={`mailto:${helpEmail}`}>{helpEmail}</a> with any
        questions.
      </span>
    );
  }, [helpEmail]);

  const getErrorMessage = useCallback(
    ({ paymentStatus, paymentDeclinedReasonCode }) => {
      if (paymentStatus === "rejectedasfraud") {
        return (
          <span>
            We&apos;re sorry, the payment was rejected due to suspected fraud.
            Please contact your card provider, try a different card, or{" "}
            {contactUs()}
          </span>
        );
      }

      if (paymentStatus === "unknownfailure") {
        return (
          <span>
            There was a problem finalizing your payment. Please DO NOT try
            again. Contact help@flocknote.com about the status of your payment.
          </span>
        );
      }

      if (paymentStatus === "declined") {
        switch (paymentDeclinedReasonCode) {
          case "ADDRESS_MISMATCH":
            return (
              <span>
                We&apos;re sorry, the payment was declined because the provided
                address does not match. Please update the card zip code, or{" "}
                {contactUs()}
              </span>
            );
          case "CARD_CANNOT_TRANSACT":
            return (
              <span>
                We&apos;re sorry, the payment was declined because the card
                cannot transact. Please try a different card, or {contactUs()}
              </span>
            );
          case "CVV_IS_REQUIRED":
            return (
              <span>
                We&apos;re sorry, the payment was declined because the CVV is
                required. Please re-enter your card, or {contactUs()}
              </span>
            );
          case "CVV_MISMATCH":
            return (
              <span>
                We&apos;re sorry, the payment was declined because the CVV does
                not match. Please update your card, or {contactUs()}
              </span>
            );
          case "EXPIRED":
            return (
              <span>
                We&apos;re sorry, the payment was declined because the payment
                method has expired. Please update your card, or {contactUs()}
              </span>
            );
          case "GENERAL_DECLINE":
          case "LOST_OR_STOLEN":
          case "REJECTED_BY_ISSUING_BANK":
            return (
              <span>
                We&apos;re sorry, the payment was declined by the issuing bank.
                Please contact your bank or card provider, try a different card,
                or {contactUs()}
              </span>
            );
          case "INSUFFICIENT_FUNDS":
            return (
              <span>
                We&apos;re sorry, the payment was declined due to insufficent
                funds. Please contact your bank or card provider, try a
                different card, or {contactUs()}
              </span>
            );
          case "UNSUPPORTED_CARD_TYPE":
            return (
              <span>
                We&apos;re sorry, this card type is not supported. Please try a
                different card, or {contactUs()}
              </span>
            );

          default:
            return (
              <span>
                We&apos;re sorry, your payment was declined. Please try a
                different card, or {contactUs()}
              </span>
            );
        }
      }

      return (
        <span>
          We&apos;re sorry, your payment failed. Please try again, try a
          different card, or {contactUs()}
        </span>
      );
    },
    [contactUs]
  );

  const shouldPoll =
    isForCurrentScreen &&
    paymentHasBeenSubmitted &&
    !paymentResultIsResolved &&
    !recurringPaymentsOnlySuccess &&
    !paymentIDAlreadyFailed;

  useEffect(() => {
    if (recurringPaymentsOnlySuccess) {
      // If it's a recurring payment and the startDate is in the future, then no paymentID is returned to be used by getPaymentStatus
      // This allows useCurrentPaymentScreen to know to move to the success screen
      dispatch({
        type: "setRecurringPaymentsOnlySuccess",
        payload: recurringPaymentsOnlySuccess,
      });
      return;
    }

    if (!shouldPoll || pollCount >= NUM_SECONDS_TO_POLL) return;

    const pollCountTimeout = setTimeout(() => {
      setPollCount((count) => count + 1);
    }, 1000);

    const getPaymentStatusCall = API.getPaymentStatus({ paymentID });

    getPaymentStatusCall
      .post()
      .then((res) => {
        const { paymentStatus: status, paymentDeclinedReasonCode } = res;

        dispatch({
          type: "setPollingPaymentResponse",
          payload: res,
        });

        // clearTimeout if we should stop polling
        const shouldKeepPolling =
          status === "attempted" ||
          status === "staged" ||
          status === "processing";
        if (!shouldKeepPolling) clearTimeout(pollCountTimeout);

        // set error message if payment failed
        const paymentFailed =
          status !== "attempted" &&
          status !== "staged" &&
          status !== "processing" &&
          status !== "completed";

        if (paymentFailed) {
          const errorMessage = getErrorMessage({
            status,
            paymentDeclinedReasonCode,
          });
          setSubmitError(errorMessage);
          setIsSubmitting(false);
          setDeclinedPaymentId(paymentID);
          setPollCount(0);

          // hit API endpoint to acknowledge that the decline notice was displayed to the user
          // (so we know we don't need to notify them via email etc)
          API.declineReceived({ paymentID }).post();
        }
      })
      .catch((err) => {
        dispatch({
          type: "setPollingPaymentResponse",
          payload: err,
        });

        // something went wrong... normal "payment failed" scenarios should still have success: true and be handled above
        setSubmitError(
          `Something went wrong! Please try again, or contact help@flocknote.com any questions.`
        );
        setIsSubmitting(false);
        setPollCount(0);
      });

    return () => {
      if (getPaymentStatusCall.cancel) getPaymentStatusCall.cancel();
    };
  }, [
    shouldPoll,
    pollCount,
    paymentID,
    declinedPaymentId,
    setDeclinedPaymentId,
    dispatch,
    setIsSubmitting,
    setSubmitError,
    helpEmail,
    recurringPaymentsOnlySuccess,
    getErrorMessage,
    NUM_SECONDS_TO_POLL,
  ]);

  useEffect(() => {
    if (pollCount !== NUM_SECONDS_TO_POLL) return;
    dispatch({
      type: "setPollingPaymentResponse",
      payload: {
        success: true,
        paymentStatus: "responseTimedOut",
      },
    });
  }, [pollCount, dispatch, NUM_SECONDS_TO_POLL]);
}

// TODO: REFACTOR FOR ISRECURRING
const isRecurringPaymentsOnlySuccess = (submitPaymentResponse, startDate) => {
  const recurringSuccessfullySetUp =
    submitPaymentResponse?.success && submitPaymentResponse?.recurringSetUp;

  return recurringSuccessfullySetUp;
};
